import { BaseApiClient } from './base-client'
import { API_ROUTES, interpolateRoute } from '../api-routes'

export interface ElevenLabsVoice {
  voice_id: string
  name: string
  labels: {
    accent: string
    description?: string
    age: string
    gender: string
    use_case: string
  }
  preview_url: string
  description: string | null
}

export interface VoiceOption {
  id: string
  name: string
  accent: string
  previewUrl: string
  labels: {
    description: string
    age: string
    gender: 'male' | 'female'
    use_case: string
  }
}

class ElevenLabsApiClient extends BaseApiClient {
  async fetchVoices(query?: string): Promise<VoiceOption[]> {
    try {
      const queryParams = query ? { query } : {}
      const response = await this.request<{
        data: { voices: ElevenLabsVoice[] }
      }>(`${API_ROUTES.ELEVENLABS.VOICES}${this.buildQueryString(queryParams)}`)

      // Transform ElevenLabs voices to our VoiceOption format
      const voices = response.data.voices.map(voice => ({
        id: voice.voice_id,
        name: voice.name,
        accent: voice.labels?.accent || 'american',
        previewUrl: voice.preview_url,
        labels: {
          description:
            voice.labels?.description ||
            voice.description ||
            'No description available',
          age: voice.labels?.age || 'adult',
          gender:
            (voice.labels?.gender?.toLowerCase() as 'male' | 'female') ||
            'male',
          use_case: voice.labels?.use_case || 'general',
        },
      }))

      return voices
    } catch (error) {
      console.error('Error fetching ElevenLabs voices:', error)
      throw error
    }
  }

  async generateSpeech(voiceId: string, text: string): Promise<string> {
    try {
      const url = `${this.baseUrl}${interpolateRoute(API_ROUTES.ELEVENLABS.TEXT_TO_SPEECH, { voiceId })}`

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...this.defaultHeaders,
        },
        body: JSON.stringify({
          text,
          model_id: 'eleven_multilingual_v2',
          voice_settings: {
            stability: 0.5,
            similarity_boost: 0.5,
          },
        }),
      })

      if (!response.ok) {
        const errorText = await response.text()
        console.error(`TTS API error (${response.status}):`, errorText)
        throw new Error(
          `Failed to generate speech: ${response.status} ${errorText}`,
        )
      }

      const audioBlob = await response.blob()
      return URL.createObjectURL(audioBlob)
    } catch (error) {
      console.error('Error generating speech:', error)
      throw error
    }
  }
}

export const elevenLabsApiClient = new ElevenLabsApiClient()
